/** La barra lateral: oscura, con secciones agrupadas y el corte de datos fijo en su pie.
 *
 * Forma tomada de la referencia del cliente (HPE AI Agent): superficie oscura contra el área de
 * trabajo clara, encabezados de sección en mayúsculas pequeñas, el item activo resaltado, y el
 * corte de datos anclado al fondo — es el dato que un analista necesita ver siempre, no uno que
 * haya que ir a buscar.
 */
import type { Fuente, Idioma, Salud, SesionResumen } from "../lib/tipos";
import { bi, t } from "../lib/i18n";
import { forwardRef, useEffect, useState } from "react";
import { Logo } from "./Logo";

export const BarraLateral = forwardRef<HTMLDivElement, {
  idioma: Idioma;
  salud: Salud | null;
  fuentes: Fuente[];
  sesiones: SesionResumen[];
  sesionId: string | null;
  tema: "claro" | "oscuro" | "sistema";
  usuario: string | null;
  alSalir?: () => void;
  alNueva: () => void;
  alAbrir: (id: string) => void;
  alBorrar: (id: string) => void;
  alIdioma: (i: Idioma) => void;
  alTema: (t: "claro" | "oscuro" | "sistema") => void;
  /** En móvil la barra vive en un cajón: fuera del flujo y detrás de un botón de menú. */
  enCajon?: boolean;
  abierta?: boolean;
  /** Plegada en escritorio: ocupa 0 px y sale del orden de tabulación. */
  plegada?: boolean;
}>(function BarraLateral({ idioma, salud, fuentes, sesiones, sesionId, tema, usuario, alNueva, alAbrir,
                           alBorrar, alIdioma, alTema, alSalir, enCajon, abierta, plegada }, ref) {
  const txt = t(idioma);
  // Cerrado por defecto, y lo que el usuario decida se recuerda. `localStorage` puede no existir
  // —ventana privada, datos bloqueados—, así que se lee y se escribe dentro de un try: la barra
  // tiene que pintarse igual aunque no haya dónde guardar nada.
  const [fuentesAbiertas, setFuentesAbiertas] = useState(() => {
    try {
      return localStorage.getItem("tim.fuentes") === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("tim.fuentes", fuentesAbiertas ? "1" : "0");
    } catch {
      /* sin almacenamiento: se pliega igual, solo que no se recuerda */
    }
  }, [fuentesAbiertas]);

  // La procedencia del CONJUNTO, para que plegado siga diciendo si el dato es de Essity.
  const origenes = [...new Set(fuentes.map((f) => f.origen))];
  const resumenProcedencia = origenes.length === 0 ? null
    : origenes.length > 1 ? "mixto" : origenes[0] === "sintetico" ? null : origenes[0];

  return (
    // `inert` cuando el cajón está cerrado: sin eso el tabulador entra en una barra invisible y
    // el foco desaparece de la pantalla. Es la parte que se olvida al hacer un drawer.
    <aside id="lateral" ref={ref}
           className={`lateral${enCajon ? " en-cajon" : ""}${abierta ? " abierta" : ""}${plegada ? " plegada" : ""}`}
           inert={(enCajon && !abierta) || plegada ? true : undefined}
           aria-hidden={(enCajon && !abierta) || plegada ? true : undefined}>
      <header className="marca">
        <Logo alto={20} />
      </header>

      <button className="boton nuevo" onClick={alNueva}>
        <span aria-hidden="true">+</span> {txt.nuevaConversacion}
      </button>

      <nav className="bloque" aria-label={txt.analisis}>
        <h2>{txt.analisis}</h2>
        <ul className="nav">
          <li><span className="nav-item activo"><span aria-hidden="true">◆</span> TIM {txt.agente}</span></li>
        </ul>
      </nav>

      <section className="bloque flexible">
        <h2>{txt.historial}</h2>
        {sesiones.length === 0 && <p className="vacio">{txt.sinConversaciones}</p>}
        <ul className="sesiones">
          {sesiones.map((s) => (
            <li key={s.id} className={s.id === sesionId ? "activa" : undefined}>
              <button className="sesion" onClick={() => alAbrir(s.id)} title={s.titulo}>
                <span>{s.titulo}</span>
                <em>{s.turnos}</em>
              </button>
              <button className="borrar" onClick={() => alBorrar(s.id)} aria-label={`${txt.borrar}: ${s.titulo}`}>×</button>
            </li>
          ))}
        </ul>
      </section>

      {/* LAS FUENTES, PLEGADAS. Medido en una ventana de 1024x768: la barra tenía 850 px de
          contenido —se desbordaba— y las siete fuentes se llevaban 418, la mitad, mientras el
          historial se quedaba con 64. El catálogo es la vista de «¿qué datos hay?», que se mira
          al principio y pocas veces después; el historial es lo que se navega a diario.
          Plegado conserva lo único que se lee de un vistazo: cuántas son y si el dato es real. */}
      <section className="bloque">
        <button type="button" className="bloque-titulo" aria-expanded={fuentesAbiertas}
                aria-controls="lista-fuentes" onClick={() => setFuentesAbiertas((v) => !v)}>
          <span className="chevron" aria-hidden="true">{fuentesAbiertas ? "⌄" : "›"}</span>
          <h2>{txt.catalogo}</h2>
          <em className="cuenta">{fuentes.length}</em>
          {resumenProcedencia && (
            <span className={`chip procedencia-${resumenProcedencia}`}
                  title={txt.procedenciaLarga[resumenProcedencia]}>
              <span className="signo-aviso" aria-hidden="true">{resumenProcedencia === "real" ? "🔒" : "⚠"}</span>
            </span>
          )}
        </button>
        <ul className="fuentes" id="lista-fuentes" hidden={!fuentesAbiertas}>
          {fuentes.map((f) => (
            <li key={f.fuente_id} title={`${f.sistema_origen} · ${txt.owner}: ${f.owner}`}>
              <strong>{bi(f.nombre, idioma)}</strong>
              <span className="owner">{f.owner}</span>
              {!f.declarada_en_slide && <span className="chip aviso" title={txt.asumidaLargo}>{txt.asumida}</span>}
              {/* La lista de fuentes es donde se ve de un vistazo QUÉ mitad del mundo es real, y
                  es la vista que responde «¿ya llegó el cluster 2?». Solo se marca lo que no es
                  sintético: marcar las siete no distinguiría nada. */}
              {f.origen !== "sintetico" && (
                <span className={`chip procedencia-${f.origen}`} title={txt.procedenciaLarga[f.origen]}>
                  <span className="signo-aviso" aria-hidden="true">{f.origen === "real" ? "🔒" : "⚠"}</span>
                  {txt.procedencia[f.origen]}
                </span>
              )}
            </li>
          ))}
        </ul>
      </section>

      <footer className="lateral-pie">
        {salud && <p className="corte">{txt.corte}: <strong>{salud.corte_datos}</strong></p>}
        <div className="lateral-controles">
          <div className="grupo" role="group" aria-label={txt.idioma}>
            {(["en", "es"] as const).map((i) => (
              <button key={i} className={i === idioma ? "sel" : ""} onClick={() => alIdioma(i)} aria-pressed={i === idioma}>
                {i.toUpperCase()}
              </button>
            ))}
          </div>
          <div className="grupo" role="group" aria-label={txt.tema}>
            {(["claro", "sistema", "oscuro"] as const).map((x) => (
              <button key={x} className={x === tema ? "sel" : ""} onClick={() => alTema(x)} aria-pressed={x === tema}
                      aria-label={x}>
                {x === "claro" ? "☀" : x === "oscuro" ? "☾" : "◐"}
              </button>
            ))}
          </div>
          {/* Solo cuando el acceso está cerrado: con la API abierta no hay de dónde salir, y un
              botón que no hace nada es peor que no tenerlo. */}
          {/* El nombre va PEGADO al botón de salir: las dos cosas contestan la misma pregunta
              —quién soy y cómo dejo de serlo— y con dos personas usando la misma pantalla en una
              demo, «¿con cuál de los dos estoy dentro?» deja de ser una pregunta retórica. */}
          {usuario && <span className="usuario" title={usuario}>{txt.sesionDe} <strong>{usuario}</strong></span>}
          {salud?.acceso === "token" && alSalir && (
            <button className="salir" onClick={alSalir}>{txt.accesoSalir}</button>
          )}
          {salud && <span className="version">v{salud.version} · {salud.modo}</span>}
        </div>
      </footer>
    </aside>
  );
});
